import * as BABYLON from 'babylonjs';
import * as BABYLONGUI from 'babylonjs-gui';
import PointEvents from '../Enumerations/ViewerEnums';
import PointConstants from '../constants/ViewerConstants';
import PointViewer from './PointViewer';
import XmlLoader2 from './XmlLoader2';
import CustomInputText from '../components/CustomInputText';
import * as Inovx from '@shared';
import BoxManager from './BoxManager';
import Draco3d from 'draco3d';
import ViewerActions from '../../actions/ViewerActions';
import ViewerStore from '../../stores/ViewerStore';
import { BlackAndWhiteScanPositionColor, BlackAndWhiteScanPositionGlowColor, isBlackAndWhiteScanPosition, isScanPositionView } from '../../helpers/viewMedia';
import { getBabylonSceneSource } from '../../Utilities/BabylonSceneLoader';
import GLBManager from './GLBManager';
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
import PointLoadWorker from '../workers/PointLoadWorker';
import GLBLoadWorker from '../workers/GLBLoadWorker';
import PointCloudManager from './PointCloudManager';
import { TextBlock } from 'babylonjs-gui';
import { flip } from '@syncfusion/ej2-react-popups';
import { getCurrentWorkerAuthContext, handleWorkerAuthRefreshMessage } from '../workers/workerAuthHost';

interface PointSceneOptions {
    showTripods?: boolean,
    scanSphereColor?: string,
    imageSphereColor?: string,
    tagVolumeColor?: string,
    measurementColor?: string
}

interface ILabel {
    label: BABYLON.Mesh,
    labelControl: BABYLONGUI.Container,
    line?: BABYLON.LinesMesh,
    lineData?: { color: string },
    annotationData: IAnnotationDisplayItem,
    pickMesh: BABYLON.Mesh
}

class PointSceneManager {
    private readonly DefaultScanSphereColor = "#00FFFB";
    private readonly ScanSphereGlowColor = "#071F1C";
    private readonly BlackAndWhiteScanSphereGlowColor = BlackAndWhiteScanPositionGlowColor;
    private readonly DefaultImageSphereColor = "#F59300";
    private readonly ImageSphereGlowColor = "#2A1600";
    private readonly SelectedTagColor = "#008080";
    private readonly OccludedTagAlpha = 0.55;
    private readonly OccludedTagLineAlpha = "99";

    private _container: HTMLDivElement;
    private _selectedView: IView360Detail;
    private _views: IView360Detail[];
    private _selectedLabel: ILabel = null;
    private _preSelectAnnControlColor: string = null;
    private _annotationsReadOnly: boolean = false;

    private _initialized: boolean = false;
    private _switchingViews: boolean = false;
    private _animationDone: boolean = false;
    private _displayObstructedLabels: boolean = false;
    private _scanMaterial: BABYLON.StandardMaterial;
    private _scanHighlightMaterial: BABYLON.StandardMaterial;
    private _blackAndWhiteScanMaterial: BABYLON.StandardMaterial;
    private _blackAndWhiteScanHighlightMaterial: BABYLON.StandardMaterial;
    private _image360Material: BABYLON.StandardMaterial;
    private _image360HighlightMaterial: BABYLON.StandardMaterial;

    private _tripods: BABYLON.Mesh[];
    private _tripodsByViewId: Map<string, BABYLON.Mesh> = new Map();
    private _highlightedTripod: BABYLON.Mesh = null;

    private _viewer: PointViewer;
    
    private _options: PointSceneOptions;
    private _tagHeightFactor: number = 25;
    private _lastPointerPosition: BABYLON.Vector2 | null = null;
    private _moveThreshold: number = 5; // Threshold distance in pixels to consider a move significant.
    public events: EventTarget;

    private _glowLayer: BABYLON.GlowLayer;

    private _labels: ILabel[] = [];
    private _occludedAnnotations: string[] = [];

    private _pointCloudManager: PointCloudManager;
    private allLoaded: boolean = false;
    private _pointerMoved = false;
    private _light: BABYLON.HemisphericLight;
    private get scene() { return this._viewer.scene; }
    private get camera() { return this._viewer.camera; }
    private get boxManager() { return this._viewer.boxManager; }

    public get viewer() { return this._viewer; }
    public get isBusy() { return !this._initialized || this._switchingViews; }
    
    constructor(div: HTMLDivElement, selectedView: IView360Detail, views: IView360Detail[], options?: PointSceneOptions) {

        this._container = div;
        this._views = views;
        this._selectedView = selectedView;
        this._options = {
            showTripods: options?.showTripods ?? false,
            scanSphereColor: options?.scanSphereColor ?? this.DefaultScanSphereColor,
            imageSphereColor: options?.imageSphereColor ?? this.DefaultImageSphereColor,
            tagVolumeColor: options?.tagVolumeColor,
            measurementColor: options?.measurementColor
        }

        this.events = new EventTarget();
    }

    public dispose() {
        this._glowLayer?.dispose();
        this._glowLayer = null;

        this._cleanupTripods(true);
        this._clearAnnotations();

        this._viewer?.dispose();
    }

    private async loadGLBLocal(url: string, wasmPath: string): Promise<any[]> {

        const sceneSource = getBabylonSceneSource(url, ".glb");
        return BABYLON.SceneLoader.ImportMeshAsync(null, sceneSource.rootUrl, sceneSource.sceneFilename, this.scene, null, sceneSource.pluginExtension).then((result): any[] => {

            var glbMessage = {
                payload: result.meshes as BABYLON.Mesh[],

            };

            var serializable: any[] = [];

            for (var i = 0; i < glbMessage.payload.length; i++) {
                serializable.push(((glbMessage.payload[i]) as any).Geometry.indices.buffer);
                serializable.push(((glbMessage.payload[i]) as any).Geometry.normals.buffer);
                serializable.push(((glbMessage.payload[i]) as any).Geometry.positions.buffer);
                serializable.push(((glbMessage.payload[i]) as any).Geometry.colors.buffer);
            }

            return serializable;
        }).catch((error): any[] => {
            console.error("Error loading GLB file:", error);
            return [];
        });
    }

    private _cleanupTripods(disposeMaterials: boolean) {
        if (this._tripods) {
            this._tripods.forEach(pod => {
                this._glowLayer?.removeIncludedOnlyMesh(pod);
                pod.dispose();
            });
        }
        this._tripods = [];
        this._tripodsByViewId.clear();
        this._highlightedTripod = null;

        if (disposeMaterials) {
            this._scanHighlightMaterial?.dispose();
            this._scanHighlightMaterial = null;
            this._scanMaterial?.dispose();
            this._scanMaterial = null;
            this._blackAndWhiteScanHighlightMaterial?.dispose();
            this._blackAndWhiteScanHighlightMaterial = null;
            this._blackAndWhiteScanMaterial?.dispose();
            this._blackAndWhiteScanMaterial = null;
            this._image360HighlightMaterial?.dispose();
            this._image360HighlightMaterial = null;
            this._image360Material?.dispose();
            this._image360Material = null;
        }
    }

    private _clearAnnotations() {
        this._occludedAnnotations = [];
        this.boxManager.clearBoxes();
        this._labels.forEach(labelData => {
            this._deleteLabel(labelData);
        });
        this._labels = [];
    }


    private _selectLabel(label: ILabel) {

        if (label == this._selectedLabel)
            return;

        this.boxManager.setSelectedBox("");

        // Clear the previous selection
        if (this._selectedLabel) {
            this._selectedLabel.labelControl.color = this._preSelectAnnControlColor;
            this._preSelectAnnControlColor = null;
            if (this._selectedLabel.lineData)
                this._selectedLabel.lineData.color = this._selectedLabel.labelControl.color + this._selectedLabel.lineData.color.substring(7, 9); // add optional transparency value
        }

        // Set the new selection
        if (label) {
            this._preSelectAnnControlColor = (label.labelControl as any).originalColor;
            label.labelControl.color = this.SelectedTagColor;
            if (label.lineData) {
                label.lineData.color = label.labelControl.color + label.lineData.color.substring(7, 9); // add optional transparency value
            }

            if (this._isBoxLabel(label)) {
                this.boxManager.setSelectedBox(label.annotationData.ID);
            }
        }

        this._selectedLabel = label;
        
    }

    private _isBoxLabel(label: ILabel) {
        return label.annotationData.TargetBoxMin;
    }

    private _getLabelBoxDimensions(label: ILabel) {
        const annotation = label.annotationData;

        let boxPosition: BABYLON.Vector3;
        let boxSize: BABYLON.Vector3;
        if (annotation.TargetBoxMin) {
            boxPosition = new BABYLON.Vector3((annotation.TargetBoxMin.X + annotation.TargetBoxMax.X) / 2, (annotation.TargetBoxMin.Y + annotation.TargetBoxMax.Y) / 2, (annotation.TargetBoxMin.Z + annotation.TargetBoxMax.Z) / 2);
            boxSize = new BABYLON.Vector3(annotation.TargetBoxMax.X - annotation.TargetBoxMin.X, annotation.TargetBoxMax.Y - annotation.TargetBoxMin.Y, annotation.TargetBoxMax.Z - annotation.TargetBoxMin.Z);
        }
        return { boxPosition, boxSize };
    }

    private _project2DPointTo3D = (x: number, y: number) => {
        // Convert screen coordinates to world coordinates
        let screenCoords = new BABYLON.Vector3(x, y, 0);
        let worldCoords = BABYLON.Vector3.Unproject(screenCoords,
            this.scene.getEngine().getRenderWidth(),
            this.scene.getEngine().getRenderHeight(),
            BABYLON.Matrix.Identity(),
            this.camera.getViewMatrix(),
            this.camera.getProjectionMatrix());

        return worldCoords;
    }
    private _createAnnotation(annotation: IAnnotationDisplayItem, tagDisplayXml: string) {

        var displayObstructed = this._displayObstructedLabels;
        const xmlLoader = new XmlLoader2(annotation);
        xmlLoader.loadLayoutFromString(tagDisplayXml, null);

        const annControl: BABYLONGUI.Rectangle = xmlLoader.getNodeById("root");

        var textBlock = annControl.children[0] as TextBlock;

        annControl.horizontalAlignment = BABYLONGUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
        annControl.verticalAlignment = BABYLONGUI.Control.VERTICAL_ALIGNMENT_CENTER;
        const baseUrl = UrlUtilities.getBaseApplicationUrl();
        this._updateSourceUrls(annControl, baseUrl);

        let boxPosition: BABYLON.Vector3;
        let boxDimensions: BABYLON.Vector3;

        if (annotation.TargetBoxMin) {
            boxPosition = new BABYLON.Vector3((annotation.TargetBoxMin.X + annotation.TargetBoxMax.X) / 2, (annotation.TargetBoxMin.Y + annotation.TargetBoxMax.Y) / 2, (annotation.TargetBoxMin.Z + annotation.TargetBoxMax.Z) / 2);
            boxDimensions = new BABYLON.Vector3(annotation.TargetBoxMax.X - annotation.TargetBoxMin.X, annotation.TargetBoxMax.Y - annotation.TargetBoxMin.Y, annotation.TargetBoxMax.Z - annotation.TargetBoxMin.Z);
        }

        if (annotation.Target3D) {
            const index = this._labels.length;

            const attachmentSphere = BABYLON.MeshBuilder.CreateSphere('annSphere' + index, { diameter: 0.1 }, this.scene);

            let domePosition: BABYLON.Vector3;
            if (boxPosition) {
                domePosition = boxPosition;
            }
            else {
                domePosition = this._getVectorFromViewData(annotation.Target3D);
            }

            annControl.children[0].fontSize = annControl.children[0].fontSizeInPixels + "px";
            const context = document.createElement("canvas").getContext("2d");
            context.font = `${annControl.children[0].fontSize} sans-serif`;  // You can specify the font you are using
            const width = context.measureText((annControl.children[0] as TextBlock).text).width + textBlock.paddingLeftInPixels + textBlock.paddingRightInPixels + annControl.thickness * 2;
            const height = this._tagHeightFactor;

            const boxHeight = height / 100;
            const boxWidth = width / 100;

            const boxOptions = {
                width: boxWidth,
                height: boxHeight
            };

            var labelPlane = BABYLON.MeshBuilder.CreatePlane(annotation.ID, boxOptions, this.scene);

            if (displayObstructed)
                labelPlane.renderingGroupId = 1;

            labelPlane.billboardMode = BABYLON.AbstractMesh.BILLBOARDMODE_ALL;
            var towardsCamDir = domePosition.subtract(this.camera.position).normalize();

            labelPlane.position = domePosition.subtract((new BABYLON.Vector3(towardsCamDir.x, 0, towardsCamDir.z)).scale(.1));

            var tagDistanceToCamera = BABYLON.Vector3.Distance(this.camera.position, domePosition);

            //var distanceScaleFactor = Math.max(1, tagDistanceToCamera);
            var distanceScaleFactor = tagDistanceToCamera / 2.5;
            distanceScaleFactor = distanceScaleFactor < 1 ? 1 : distanceScaleFactor > 4 ? 4 : distanceScaleFactor;

            var fov = this.camera.fov; // Field of view in radians
            var viewportHeight = this.scene.getEngine().getRenderHeight(); // Viewport height in pixels
            var cameraDistanceToScreen = 1 / Math.tan(fov / 2); // How far the camera "sees"

            var downShift = .075 * distanceScaleFactor;
            labelPlane.position.y -= downShift;

            const labelPoint2D = new BABYLON.Vector2(annControl.leftInPixels + annControl.widthInPixels / 2, annControl.topInPixels);
            const labelPoint2Din3D = this._project2DPointTo3D(labelPoint2D.x, labelPoint2D.y);

            // To change the size of the tag ONLY change this scaling! The texture has been sized
            // and fit to the mesh in other code, and that should NOT be touched!
            labelPlane.scaling = new BABYLON.Vector3(.25 * distanceScaleFactor, .25 * distanceScaleFactor, 1);
            var linePoints: BABYLON.Vector3[] = [];
            var lineColors: BABYLON.Color4[] = [];
            lineColors.push(BABYLON.Color4.FromColor3(BABYLON.Color3.White()));
            lineColors.push(BABYLON.Color4.FromColor3(BABYLON.Color3.White()));

            // Make the line points relative to domePosition
            linePoints.push(BABYLON.Vector3.Zero()); // Relative to domePosition
            linePoints.push(labelPlane.position.subtract(domePosition).add(new BABYLON.Vector3(0, (boxOptions.height / 2) * labelPlane.scaling.y, 0)));

            var leaderLine = (BABYLON.MeshBuilder.CreateLines("line", {
                colors: lineColors,
                points: linePoints,
                updatable: true
            }, this.scene));

            // Set the leaderLine's position to domePosition
            leaderLine.position = domePosition;

            leaderLine.position = domePosition;
            leaderLine.isPickable = false;

            attachmentSphere.position = domePosition;
            attachmentSphere.setEnabled(false);

            let lineData = { color: annControl.color };
            if (annotation.Target2D) {
                annControl.linkOffsetX = annotation.Target2D.X;
                annControl.linkOffsetY = annotation.Target2D.Y;
            }
            else {
                // Annotations without an offset (usually icons) do not need a connector line.
                if (annControl.linkOffsetXInPixels == 0 && annControl.linkOffsetYInPixels == 0) {
                    lineData = null;
                }
            }

            (annControl as any).originalColor = annControl.color;

            const isOccluded = this._occludedAnnotations.includes(annotation.ID);
            if (isOccluded) {
                annControl.alpha = this.OccludedTagAlpha;

                if (lineData)
                    lineData.color += this.OccludedTagLineAlpha;
            }
            else {
                // Swap the alpha to only apply to the background color
                annControl.color = this.hexToRgb(annControl.color, 1.0);
                annControl.background = this.hexToRgb(annControl.background, annControl.alpha);
                annControl.alpha = 1.0;
            }


            var boxClone = labelPlane.clone(labelPlane.name);
            boxClone.id = labelPlane.id;
            labelPlane.isPickable = false;

            boxClone.visibility = 0;
            var tAM = BABYLONGUI.AdvancedDynamicTexture.CreateForMesh(labelPlane, (boxOptions.width * 100) / labelPlane.scaling.x, (boxOptions.height * 100) / labelPlane.scaling.y);

            annControl.adaptHeightToChildren = true;
            annControl.adaptWidthToChildren = true;
            annControl.height = "100%";
            annControl.width = "100%";

            (annControl.children[0] as TextBlock).resizeToFit = true;
            annControl.children[0].width = "100%";
            annControl.children[0].height = "100%";

            tAM.addControl(annControl);

            labelPlane.scaling = new BABYLON.Vector3(1, 1, 1);

            const annotationDataCopy = { ...annotation };
            //annotationDataCopy.Occluded = displayObstructed;
            const labelData: ILabel = {
                label: labelPlane,
                labelControl: annControl,
                line: leaderLine,
                lineData: lineData,
                annotationData: annotationDataCopy,
                pickMesh: boxClone
            };

            if (this._isBoxLabel(labelData))
            {
                const boxData = this._getLabelBoxDimensions(labelData);
                this.boxManager.createBox({ id: labelData.annotationData.ID, realWorldPos: boxData.boxPosition, xDim: boxData.boxSize.x, yDim: boxData.boxSize.y, zDim: boxData.boxSize.z, displayObstructed: this._displayObstructedLabels });
            }

            return labelData;
        }
    }
    private _createAnnotationLarger(annotation: IAnnotationDisplayItem, tagDisplayXml: string) {

        var displayObstructed = this._displayObstructedLabels;
        const xmlLoader = new XmlLoader2(annotation);
        xmlLoader.loadLayoutFromString(tagDisplayXml, null);

        const annControl: BABYLONGUI.Rectangle = xmlLoader.getNodeById("root");

        var textBlock = annControl.children[0] as TextBlock;

        annControl.horizontalAlignment = BABYLONGUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
        annControl.verticalAlignment = BABYLONGUI.Control.VERTICAL_ALIGNMENT_CENTER;
        const baseUrl = UrlUtilities.getBaseApplicationUrl();
        this._updateSourceUrls(annControl, baseUrl);

        let boxPosition: BABYLON.Vector3;
        let boxDimensions: BABYLON.Vector3;

        if (annotation.TargetBoxMin) {
            boxPosition = new BABYLON.Vector3((annotation.TargetBoxMin.X + annotation.TargetBoxMax.X) / 2, (annotation.TargetBoxMin.Y + annotation.TargetBoxMax.Y) / 2, (annotation.TargetBoxMin.Z + annotation.TargetBoxMax.Z) / 2);
            boxDimensions = new BABYLON.Vector3(annotation.TargetBoxMax.X - annotation.TargetBoxMin.X, annotation.TargetBoxMax.Y - annotation.TargetBoxMin.Y, annotation.TargetBoxMax.Z - annotation.TargetBoxMin.Z);
        }

        if (annotation.Target3D) {
            const index = this._labels.length;

            const attachmentSphere = BABYLON.MeshBuilder.CreateSphere('annSphere' + index, { diameter: 0.1 }, this.scene);

            let domePosition: BABYLON.Vector3;
            if (boxPosition) {
                domePosition = boxPosition;
            }
            else {
                domePosition = this._getVectorFromViewData(annotation.Target3D);
            }

            annControl.children[0].fontSize = annControl.children[0].fontSizeInPixels + "px";
            const context = document.createElement("canvas").getContext("2d");
            context.font = `${annControl.children[0].fontSize} sans-serif`;  // You can specify the font you are using
            const width = context.measureText((annControl.children[0] as TextBlock).text).width + textBlock.paddingLeftInPixels + textBlock.paddingRightInPixels + annControl.thickness * 2;
            const height = this._tagHeightFactor;

            var scaleDiv = 10;
            var boxHeight = height / scaleDiv;
            var boxWidth = width / scaleDiv;

            var scaleFactor = 100;
            boxHeight *= scaleFactor;
            boxWidth *= scaleFactor;

            const boxOptions = {
                width: boxWidth,
                height: boxHeight
            };

            var labelPlane = BABYLON.MeshBuilder.CreatePlane(annotation.ID, boxOptions, this.scene);

            if (displayObstructed)
                labelPlane.renderingGroupId = 1;

            labelPlane.billboardMode = BABYLON.AbstractMesh.BILLBOARDMODE_ALL;
            var towardsCamDir = domePosition.subtract(this.camera.position).normalize();

            labelPlane.position = domePosition.subtract((new BABYLON.Vector3(towardsCamDir.x, 0, towardsCamDir.z)).scale(.1));

            var tagDistanceToCamera = BABYLON.Vector3.Distance(this.camera.position, domePosition);

            //var distanceScaleFactor = Math.max(1, tagDistanceToCamera);
            var distanceScaleFactor = tagDistanceToCamera / 2.5;
            distanceScaleFactor = distanceScaleFactor < 1 ? 1 : distanceScaleFactor > 4 ? 4 : distanceScaleFactor;

            var fov = this.camera.fov; // Field of view in radians
            var viewportHeight = this.scene.getEngine().getRenderHeight(); // Viewport height in pixels
            var cameraDistanceToScreen = 1 / Math.tan(fov / 2); // How far the camera "sees"

            var downShift = .1;
            labelPlane.position.y -= downShift;

            const labelPoint2D = new BABYLON.Vector2(annControl.leftInPixels + annControl.widthInPixels / 2, annControl.topInPixels);
            const labelPoint2Din3D = this._project2DPointTo3D(labelPoint2D.x, labelPoint2D.y);

            distanceScaleFactor = 4;
            // To change the size of the tag ONLY change this scaling! The texture has been sized
            // and fit to the mesh in other code, and that should NOT be touched!
            labelPlane.scaling = new BABYLON.Vector3(.25 * distanceScaleFactor, .25 * distanceScaleFactor, 1);
            var linePoints: BABYLON.Vector3[] = [];
            var lineColors: BABYLON.Color4[] = [];
            lineColors.push(BABYLON.Color4.FromColor3(BABYLON.Color3.White()));
            lineColors.push(BABYLON.Color4.FromColor3(BABYLON.Color3.White()));

            // Make the line points relative to domePosition
            linePoints.push(BABYLON.Vector3.Zero()); // Relative to domePosition
            linePoints.push(labelPlane.position.subtract(domePosition).add(new BABYLON.Vector3(0, (boxOptions.height / 2) * labelPlane.scaling.y, 0)));

            var leaderLine = (BABYLON.MeshBuilder.CreateLines("line", {
                colors: lineColors,
                points: linePoints,
                updatable: true
            }, this.scene));

            // Set the leaderLine's position to domePosition
            leaderLine.position = domePosition;

            leaderLine.position = domePosition;
            leaderLine.isPickable = false;

            attachmentSphere.position = domePosition;
            attachmentSphere.setEnabled(false);

            let lineData = { color: annControl.color };
            if (annotation.Target2D) {
                annControl.linkOffsetX = annotation.Target2D.X;
                annControl.linkOffsetY = annotation.Target2D.Y;
            }
            else {
                // Annotations without an offset (usually icons) do not need a connector line.
                if (annControl.linkOffsetXInPixels == 0 && annControl.linkOffsetYInPixels == 0) {
                    lineData = null;
                }
            }

            (annControl as any).originalColor = annControl.color;

            const isOccluded = this._occludedAnnotations.includes(annotation.ID);
            if (isOccluded) {
                annControl.alpha = this.OccludedTagAlpha;

                if (lineData)
                    lineData.color += this.OccludedTagLineAlpha;
            }
            else {
                // Swap the alpha to only apply to the background color
                annControl.color = this.hexToRgb(annControl.color, 1.0);
                annControl.background = this.hexToRgb(annControl.background, annControl.alpha);
                annControl.alpha = 1.0;
            }


            var boxClone = labelPlane.clone(labelPlane.name);
            boxClone.id = labelPlane.id;
            labelPlane.isPickable = false;

            boxClone.scaling = new BABYLON.Vector3(.01, .01, .01);
            //boxClone.visibility = 0;
            var tAM = BABYLONGUI.AdvancedDynamicTexture.CreateForMesh(labelPlane, (boxOptions.width * scaleDiv) / labelPlane.scaling.x, (boxOptions.height * scaleDiv) / labelPlane.scaling.y);

            annControl.adaptHeightToChildren = true;
            annControl.adaptWidthToChildren = true;
            annControl.height = "100%";
            annControl.width = "100%";

            (annControl.children[0] as TextBlock).resizeToFit = true;
            annControl.children[0].width = "100%";
            annControl.children[0].height = "100%";

            tAM.addControl(annControl);

            labelPlane.scaling = new BABYLON.Vector3(1, 1, 1);

            const annotationDataCopy = { ...annotation };
            //annotationDataCopy.Occluded = displayObstructed;
            const labelData: ILabel = {
                label: labelPlane,
                labelControl: annControl,
                line: leaderLine,
                lineData: lineData,
                annotationData: annotationDataCopy,
                pickMesh: boxClone
            };


            return labelData;
        }
    }
    
    private  hexToRgb(hex:string, alpha:number) {
        // Remove the hash at the start if it's there
        var hexRGB = hex.replace(/^#/, '');

        // Parse the r, g, b values
        let bigint = parseInt(hexRGB, 16);
        let r = (bigint >> 16) & 255;
        let g = (bigint >> 8) & 255;
        let b = bigint & 255;

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    private _updateLabelPosition(label: ILabel, position: BABYLON.Vector3) {

        if (label?.label) {
            this._deleteLabel(label);
            this._labels = this._labels.filter(l => l.annotationData.ID != label.annotationData.ID);
            label.annotationData.Target3D.X = position.x;
            label.annotationData.Target3D.Y = position.y;
            label.annotationData.Target3D.Z = position.z;
            var newLabel = this._createAnnotation(label.annotationData, label.annotationData.AssetClass.TagDisplayData);
            this._labels.push(newLabel);
            this._selectedLabel = newLabel;
        }
    }

    private _updateSourceUrls(control: BABYLONGUI.Control, baseUrl: string) {
        // Check if the control is an Image and has a 'source' property
        if (control instanceof BABYLONGUI.Image && control.source) {
            // Update the source if it's a relative URL
            if (!control.source.startsWith('http://') && !control.source.startsWith('https://')) {
                control.source = `${baseUrl}/${control.source}`;
            }
        }

        // If the control has children, iterate through them as well
        if ('children' in control) {
            for (const child of (control as any).children) {
                this._updateSourceUrls(child, baseUrl);
            }
        }
    }

    private _getViewFromSphereMesh(sphere: BABYLON.AbstractMesh) {
        return this._views.find(view => view.ID == sphere.name);
    }

    private _initializeTripods(disposeMaterials: boolean = false) {
        // To Implement
        this._cleanupTripods(disposeMaterials);

        if (!this._options.showTripods) {
            return;
        }

        if (!this._scanMaterial) {
            const tripodMaterial = new BABYLON.StandardMaterial("scanMaterial", this.scene);
            tripodMaterial.alpha = 0.3;
            tripodMaterial.diffuseColor = BABYLON.Color3.FromHexString(this._options.scanSphereColor);

            const tripodGlowMaterial = tripodMaterial.clone("scanMaterial_glow");
            tripodGlowMaterial.emissiveColor = BABYLON.Color3.FromHexString(this.ScanSphereGlowColor);
            tripodGlowMaterial.alpha = 0.75;

            this._scanMaterial = tripodMaterial;
            this._scanHighlightMaterial = tripodGlowMaterial;
        }

        if (!this._image360Material) {
            const tripodMaterial = new BABYLON.StandardMaterial("image360Material", this.scene);
            tripodMaterial.alpha = 0.3;
            tripodMaterial.diffuseColor = BABYLON.Color3.FromHexString(this._options.imageSphereColor);

            const tripodGlowMaterial = tripodMaterial.clone("image360Material_glow");
            tripodGlowMaterial.emissiveColor = BABYLON.Color3.FromHexString(this.ImageSphereGlowColor);
            tripodGlowMaterial.alpha = 0.75;

            this._image360Material = tripodMaterial;
            this._image360HighlightMaterial = tripodGlowMaterial;
        }

        if (!this._blackAndWhiteScanMaterial) {
            const tripodMaterial = new BABYLON.StandardMaterial("blackAndWhiteScanMaterial", this.scene);
            tripodMaterial.alpha = 0.3;
            tripodMaterial.diffuseColor = BABYLON.Color3.FromHexString(BlackAndWhiteScanPositionColor);

            const tripodGlowMaterial = tripodMaterial.clone("blackAndWhiteScanMaterial_glow");
            tripodGlowMaterial.emissiveColor = BABYLON.Color3.FromHexString(this.BlackAndWhiteScanSphereGlowColor);
            tripodGlowMaterial.alpha = 0.75;

            this._blackAndWhiteScanMaterial = tripodMaterial;
            this._blackAndWhiteScanHighlightMaterial = tripodGlowMaterial;
        }

        this._views.map((view, index) => {
            if (view != this._selectedView) {
                const viewPosition = this._getVectorFromViewData(view.Position);
                {
                    const sphereDiameter = 1;

                    const validRotation = isScanPositionView(view);
                    const isBlackAndWhite = validRotation && isBlackAndWhiteScanPosition(view);

                    const sphere = BABYLON.MeshBuilder.CreateSphere(view.ID, { diameter: sphereDiameter }, this.scene);
                    sphere.material = validRotation
                        ? (isBlackAndWhite ? this._blackAndWhiteScanMaterial : this._scanMaterial)
                        : this._image360Material;
                    sphere.position = viewPosition;
                    sphere.metadata = { __scan: validRotation, __blackAndWhite: isBlackAndWhite };

                    sphere.actionManager = new BABYLON.ActionManager(this.scene);

                    // Change cursor to 'hand' when hovering over the sphere
                    sphere.actionManager.registerAction(new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPointerOverTrigger, (e) => {
                        if (!this.isBusy) {
                            this.scene.getEngine().getRenderingCanvas().style.cursor = 'pointer';

                            this._highlightTripod(e.source as BABYLON.Mesh);

                            const pickedView = this._getViewFromSphereMesh(sphere);
                            this.events.dispatchEvent(new CustomEvent(PointEvents.Viewer.VIEW_HIGHLIGHTED, {
                                detail: { view: pickedView }
                            }));
                        }
                    }));

                    // Revert cursor when no longer hovering over the sphere
                    sphere.actionManager.registerAction(new BABYLON.ExecuteCodeAction(BABYLON.ActionManager.OnPointerOutTrigger, (e) => {
                        if (!this.isBusy) {
                            this.scene.getEngine().getRenderingCanvas().style.cursor = '';

                            this._unhighlightTripod(e.source as BABYLON.Mesh);

                            this.events.dispatchEvent(new CustomEvent(PointEvents.Viewer.VIEW_HIGHLIGHTED, {
                                detail: { view: null }
                            }));
                        }
                    }));

                    this._tripods.push(sphere);
                    this._tripodsByViewId.set(view.ID, sphere);
                    
                }
            }
        });
    }

    private getCameraAngle(camera: BABYLON.Camera) {
        let cameraX = camera.position.x - this.camera.target.x;
        let cameraY = camera.position.z - this.camera.target.z;
        return ((this.calculateAngle(cameraX, cameraY) + 180) % 360);
    }
    
    private isMeshInFrustum(mesh: BABYLON.Mesh, camera: BABYLON.Camera) {
        let boundingInfo = mesh.getBoundingInfo();
        //let frustumPlanes = BABYLON.Frustum.GetPlanes(camera.getFrustum());

        //return boundingInfo.isInFrustum(frustumPlanes);
        return camera.isInFrustum(boundingInfo);
    }
    private _onBeforeCameraRender(camera: BABYLON.Camera) {

        if (this._pointCloudManager == null)
            return;

        this._pointCloudManager.enableSeenPointClouds(camera);
    }
    
    private _onAfterCameraRender(camera: BABYLON.Camera) {
        
    }

    private _getVectorFromViewData(srcVector: IVector3D) {
        return new BABYLON.Vector3(srcVector.X, srcVector.Y, srcVector.Z); 
    }

    private _highlightTripod(sphere: BABYLON.Mesh) {
        if (this._highlightedTripod === sphere) return;
        if (this._highlightedTripod) {
            this._unhighlightTripod(this._highlightedTripod);
        }

        sphere.metadata?.__scan ? this._highlightScan(sphere) : this._highlightImage(sphere);
        this._highlightedTripod = sphere;
    }

    private _unhighlightTripod(sphere: BABYLON.Mesh) {
        sphere.metadata?.__scan ? this._unhighlightScan(sphere) : this._unhighlightImage(sphere);
        if (this._highlightedTripod === sphere) {
            this._highlightedTripod = null;
        }
    }

    private _highlightScan(sphere: BABYLON.Mesh) {
        const highlightMaterial = sphere.metadata?.__blackAndWhite
            ? this._blackAndWhiteScanHighlightMaterial
            : this._scanHighlightMaterial;
        if (sphere.material != highlightMaterial) {
            sphere.material = highlightMaterial;
            this._glowLayer.addIncludedOnlyMesh(sphere);
        }
    }

    private _unhighlightScan(sphere: BABYLON.Mesh) {
        if (sphere.material == this._scanHighlightMaterial || sphere.material == this._blackAndWhiteScanHighlightMaterial) {
            sphere.material = sphere.metadata?.__blackAndWhite ? this._blackAndWhiteScanMaterial : this._scanMaterial;
            this._glowLayer.removeIncludedOnlyMesh(sphere);
        }
    }

    private _highlightImage(sphere: BABYLON.Mesh) {
        if (sphere.material != this._image360HighlightMaterial) {
            sphere.material = this._image360HighlightMaterial;
            this._glowLayer.addIncludedOnlyMesh(sphere);
        }
    }

    private _unhighlightImage(sphere: BABYLON.Mesh) {
        if (sphere.material == this._image360HighlightMaterial) {
            sphere.material = this._image360Material;
            this._glowLayer.removeIncludedOnlyMesh(sphere);
        }
    }

    private _hasAnnotationChanged(annotation: IAnnotationDisplayItem, updatedAnnotation: IAnnotationDisplayItem, displayObstructed:boolean): boolean {
        return annotation?.Text != updatedAnnotation?.Text ||
            annotation.Target2D?.X != updatedAnnotation.Target2D?.X ||
            annotation.Target2D?.Y != updatedAnnotation.Target2D?.Y ||
            annotation.Target3D?.X != updatedAnnotation.Target3D?.X ||
            annotation.Target3D?.Y != updatedAnnotation.Target3D?.Y ||
            annotation.Target3D?.Z != updatedAnnotation.Target3D?.Z ||
            annotation.TargetBoxMin?.X != updatedAnnotation.TargetBoxMin?.X ||
            annotation.TargetBoxMin?.Y != updatedAnnotation.TargetBoxMin?.Y ||
            annotation.TargetBoxMin?.Z != updatedAnnotation.TargetBoxMin?.Z ||
            annotation.TargetBoxMax?.X != updatedAnnotation.TargetBoxMax?.X ||
            annotation.TargetBoxMax?.Y != updatedAnnotation.TargetBoxMax?.Y ||
            annotation.TargetBoxMax?.Z != updatedAnnotation.TargetBoxMax?.Z ||
            annotation.AssetClass?.Name != updatedAnnotation.AssetClass?.Name;
    }

    private _deleteLabel(label: ILabel) {

        if (label.label != null) {
            this.scene.removeMesh(label.label);
            label.label?.dispose();
        }

        if (label.line != null) {
            this.scene.removeMesh(label.line);
            label.line?.dispose();
        }

        if (label.pickMesh != null) {
            this.scene.removeMesh(label.pickMesh);
            label.pickMesh?.dispose();
        }
    }

    private _initializeAnnotations(annotations: IAnnotationDisplayItem[], selectedAnnotation: IAnnotationDisplayItem = null, displayObstructed:boolean = false) {
        
        const deletedLabels = new Set(this._labels);
        const newLabelsList: ILabel[] = [];

        this._displayObstructedLabels = displayObstructed;
        for (const annotation of annotations) {
            let labelData = this._labels.find(label => label.annotationData.ID == annotation.ID);

            if (labelData) {
                if (this._hasAnnotationChanged(labelData.annotationData, annotation, displayObstructed)) {
                    labelData = this._createAnnotation(annotation, annotation.AssetClass.TagDisplayData);
                }
                else {
                    deletedLabels.delete(labelData);
                }
            }
            else {
                labelData = this._createAnnotation(annotation, annotation.AssetClass.TagDisplayData);
            }

            newLabelsList.push(labelData);

            if (annotation == selectedAnnotation) {
                this._selectLabel(labelData);
            }
        }

        if (!selectedAnnotation) {
            this._selectLabel(null);
        }

        this._labels = newLabelsList;

        // remove deleted labels
        deletedLabels.forEach(label => {
            this._deleteLabel(label);
        });

        // Precompute the layout of the annotations. This prevents a flicker when the annotations are first displayed.
        // Not sure why this is necessary, because it should be as a part of the initial render pass.
        //this._precomputeAnnotationLayouts();
    }

    /*
    private _precomputeAnnotationLayouts(container: BABYLONGUI.Container = this._guiTexture.rootContainer) {
        
        const textureSize = this._guiTexture.getSize();
        const context = this._guiTexture.getContext();
        const measure = new BABYLONGUI.Measure(0, 0, textureSize.width, textureSize.height);
        container._layout(measure, context);
        
    }
    */

    private _initializeScene() {
        this._glowLayer = new BABYLON.GlowLayer('glow', this.scene);

        this.scene.onPointerObservable.add((pointerInfo) => {
            if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERPICK) {
                //this._handlePointerPick(pointerInfo);
            }
            if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERUP) {
                this._handlePointerPick(pointerInfo);
            }
            if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERDOWN) {
                this._pointerMoved = false;
                const downPosition = new BABYLON.Vector2(pointerInfo.event.clientX, pointerInfo.event.clientY);
                this._lastPointerPosition = downPosition; // Record pointer position on down
            }
            if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERMOVE) {
               
                if (this._lastPointerPosition) {
                    const currentPosition = new BABYLON.Vector2(pointerInfo.event.clientX, pointerInfo.event.clientY);
                    const distance = BABYLON.Vector2.Distance(this._lastPointerPosition, currentPosition);

                    if (distance > this._moveThreshold) {
                        this._pointerMoved = true;
                    }
                }
            }
        });

        this.scene.onAfterCameraRenderObservable.add((camera) => this._onAfterCameraRender(camera));

        this.scene.onBeforeCameraRenderObservable.add((camera) => this._onBeforeCameraRender(camera));

        if (this._options.tagVolumeColor)
            this.boxManager.setBoxesColor(this._options.tagVolumeColor);

        this.boxManager.events.addEventListener(PointEvents.BoxManager.BOX_CHANGED, () => {
            if (this._selectedLabel) {
                const annotation = this._selectedLabel.annotationData;

                const box = this.boxManager.getBoxProperties(this._selectedLabel.annotationData.ID);
                const halfScaleVec = new BABYLON.Vector3(box.xDim / 2, box.yDim / 2, box.zDim / 2);
                const boxMin = box.realWorldPos.subtract(halfScaleVec);
                const boxMax = box.realWorldPos.add(halfScaleVec);
                annotation.TargetBoxMin = { X: boxMin.x, Y: boxMin.y, Z: boxMin.z };
                annotation.TargetBoxMax = { X: boxMax.x, Y: boxMax.y, Z: boxMax.z };

                this._updateLabelPosition(this._selectedLabel, box.realWorldPos);

                this.events.dispatchEvent(new CustomEvent(PointEvents.Viewer.ANNOTATION_UPDATE, {
                    detail: { annotation: annotation }
                }));
            }
        });

        this.scene.render(true);
    }

    private _handlePointerPick(pointerInfo: BABYLON.PointerInfo) {

        // If the mouse moved (the camera rotates)
        if (this._pointerMoved)
            return;

        // No picking while in measurement or volume manipulation modes
        if (this.viewer.displayMode != DisplayMode.CreateAnnotation && this.viewer.displayMode != DisplayMode.PositionAnnotation && this.viewer.displayMode != DisplayMode.Navigation)
            return;

        // Create annotation mode picking logic
        if (this.viewer.displayMode == DisplayMode.CreateAnnotation || this.viewer.displayMode == DisplayMode.PositionAnnotation) {

            var canvasRect = this._viewer.scene.getEngine().getRenderingCanvasClientRect();
            var x = pointerInfo.event.clientX - canvasRect.left;
            var y = pointerInfo.event.clientY - canvasRect.top;
            var screenPoint = new BABYLON.Vector2(x, y);
            var worldCoord = this._viewer.pointCloudManager.pickPointCloud(screenPoint, this.scene, .01);

            if (worldCoord.point != null && worldCoord.valid) {
                const point3D: IVector3D = { X: worldCoord.point.x, Y: worldCoord.point.y, Z: worldCoord.point.z };
                const point2D: IVector2D = { X: x, Y: y };
                const annotation = this.viewer.displayMode == DisplayMode.PositionAnnotation ? this._selectedLabel?.annotationData : null;
                this.events.dispatchEvent(new CustomEvent(PointEvents.Viewer.POINT3D_CLICKED, {
                    detail: { point3D: point3D, point2D: point2D, annotationID: annotation?.ID }
                }));
            }
            
            return;
        }

        // Navigation mode picking logic
        if (this.viewer.displayMode == DisplayMode.Navigation) {

            const canvasRect = this._viewer.scene.getEngine().getRenderingCanvasClientRect();
            const x = pointerInfo.event.clientX - canvasRect.left;
            const y = pointerInfo.event.clientY - canvasRect.top;
            const screenPoint = new BABYLON.Vector2(x, y);

            const pickResult = pointerInfo.pickInfo != null ? this.scene.pick(screenPoint.x, screenPoint.y) : null;

            // Pick labels
            const pickLabelsResult = this._handlePickLabels(pickResult?.pickedMesh);
            if (pickLabelsResult.labelPicked)
                return;

            // Pick assemblies
            const pickAssembliesResult = this._handlePickAssemblies(pickResult?.pickedMesh);
            if (pickAssembliesResult.assemblyPicked) {
                return;
            }

            // Pick navigation spheres (or label box)
            
            if (pickResult?.hit && pickResult.pickedMesh) {
                const pickedView = this._getViewFromSphereMesh(pickResult?.pickedMesh);
                if (pickedView) {
                    if (!this.isBusy) {
                        this.events.dispatchEvent(new CustomEvent(PointEvents.Viewer.VIEW_CLICKED, {
                            detail: { view: pickedView, event: pointerInfo.event }
                        }));
                    }
                    return;
                }
                else if (pickResult.pickedMesh.name == BoxManager.BOX_NAME) {
                    // Do nothing if the box was picked, as the related annotation is already selected.
                    return;
                }
            }

            // This has been changed to always clear selection, even if a label is not selected in the viewer.
            // We do this since a label can be selected that isn't shown in the viewer (if it is occluded, and not shown)
            // In that case, the left click in empty space should still clear the selection in other controls.
            if (pickLabelsResult.shallClearSelection) {
                this.events.dispatchEvent(new CustomEvent(PointEvents.Viewer.LABEL_SELECTION, {
                    detail: { annotation: null }
                }));
            }

            ViewerActions.resetAssemblySelection();

            return;
        }
    }

    private _handlePickAssemblies(pickedMesh: BABYLON.AbstractMesh) {

        let pickedAssembly = null;
        if (pickedMesh != null) {

            if (pickedMesh.id.indexOf(':') < 0) {
                // test if the selection is the already selected assembly.
                if (this._viewer.assemblyManager.isSelectedAssembly(pickedMesh.id)) {
                    pickedAssembly = this._viewer.assemblyManager.getSelectedAssemblyID();
                }
            }
            else {
                const [parsedAssemblyID, _, parsedInstanceID] = JSON.parse(pickedMesh.id);

                if (this._viewer.assemblyManager.hasAssembly(parsedAssemblyID, parsedInstanceID)) {
                    pickedAssembly = pickedMesh.id;

                    var bounds = this._viewer.assemblyManager.getSelectedAssemblyBounds(parsedInstanceID);
                    ViewerActions.selectAssembly(parsedInstanceID, bounds);
                    
                }
            }
        }

        return { assemblyPicked: pickedAssembly }
    }
    private _handlePickLabels(pickedMesh: BABYLON.AbstractMesh) {
        
        let hitRectangle = false;
        let labelIsSelected = false;
        let pickedLabel = null;
        // determine if any labels were picked
        if (pickedMesh != null) {
            for (var x = 0; x < this._labels.length; x++) {
                const label = this._labels[x];
                const annotationID = label.annotationData.ID;

                if (annotationID == pickedMesh.id) {
                    pickedLabel = label;

                    this.events.dispatchEvent(new CustomEvent(PointEvents.Viewer.LABEL_SELECTION, {
                        detail: {
                            annotationID: annotationID,
                            //position: { x: label.labelControl.centerX, y: label.labelControl.centerY - label.labelControl.linkOffsetYInPixels }
                            position: { x: 0, y: 0 }
                        }
                    }));

                    break;
                }
            }
        }

        return { labelPicked: pickedLabel, shallClearSelection: (!hitRectangle) }
    }

    private async _initiateImageLoadAsync(imageUrl: string) {
        return new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error(`Failed to load image at ${imageUrl}`));
            image.src = imageUrl;
        });
    }

    public async init(initialAnnotations: IAnnotationDisplayItem[], direction: BABYLON.Vector3, onViewerReady?: (viewer: PointViewer) => void) {
        const selectedView = this._selectedView;

        this._pointCloudManager = new PointCloudManager(selectedView.ScanFileData);
        // Create the viewer
        this._viewer = new PointViewer(this._container, selectedView.Position, direction as any, this._pointCloudManager, { measurementColor: this._options.measurementColor });
        this._initializeScene();

        // Show the initial annotations
        this._initializeTripods();
        this._initializeAnnotations(initialAnnotations);
        this.setMeasurements(ViewerStore.getMeasurements());
        this._initialized = true;

        // Notify the caller that the viewer has been created
        onViewerReady?.(this._viewer);
    }

    public async changeSelectedView(selectedView: IView360Detail, initialAnnotations: IAnnotationDisplayItem[], assemblyTemplates: IAssemblyTemplate[], assemblies: IAssembly[], glbSources: IGraphicSource[],
        cameraProperties?: IPanoCameraProperties, showTripods?: boolean, onViewChanged?: () => void) {
        if (this.isBusy) {
            // This shouldn't happen, as the navigation spheres should be hidden while switching views.
            // But just in case...
            return;
        }

        this._switchingViews = true;
        this._animationDone = false;
        this._options.showTripods = showTripods ?? false;

        // Cleanup
        this._clearAnnotations();

        const imageUrl = selectedView.ImageUrl;

        // Initiate the loading of the new image
        const loadImageAsyncRequest = new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error(`Failed to load image at ${imageUrl}`));
            image.src = imageUrl;
        });

        // Run the camera animation (if the viewer already exists)
        let cameraFOV = this.camera.fov;
        const curViewPosition = this._getVectorFromViewData(this._selectedView.Position);
        const newViewPosition = this._getVectorFromViewData(selectedView.Position);
        const directionToTarget = this._getHorizontalNavigationDirection(curViewPosition, newViewPosition);

        const animateCameraAsyncRequest = this._viewer.changeCameraDirection(directionToTarget, cameraFOV * 0.6, true);
        
        // Wait for the image to load
        const image = await loadImageAsyncRequest;

        // Wait for the camera animation to complete and then update the dome texture
        await animateCameraAsyncRequest;

        this._cleanupTripods(false);
        this._animationDone = true;

        // load points now
        this._pointCloudManager.clearPointClouds();

        if (this._light == null) {
            var light = new BABYLON.HemisphericLight("light", new BABYLON.Vector3(0, 1, 0), this.scene);

            // Default intensity is 1. Let's dim the light a small amount
            light.intensity = 0.7;

            this._light = light;
        }

        var glbManager = this._viewer.assemblyManager;

        for (const template of assemblyTemplates) {
            const graphicsFileUrl = template.GraphicsFileUrl.startsWith("http")
                ? template.GraphicsFileUrl
                : Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + template.GraphicsFileUrl;

            await glbManager.loadGLBAssembly(template.ID, template.Name, graphicsFileUrl);
        }

        for (const assembly of assemblies) {
            await glbManager.createOrUpdateAssembly(assembly);
        }

        for (var dataIndex = 0; dataIndex < selectedView.ScanFileData.ScanDatas.length; dataIndex++) {
            if (selectedView.ScanFileData.ScanDatas[dataIndex].SliceIndex >= 0) {
                this.loadPointCloud(selectedView.ScanFileData.ScanDatas[dataIndex].ScanFileUrl, 1, selectedView.ScanFileData.ScanDatas[dataIndex].SliceIndex, selectedView.ScanFileData.ScanDatas[dataIndex].IsNearSlice);
            }
        }

        this._viewer.setCameraPosition(newViewPosition);

        if (cameraProperties) {
            if (cameraProperties.fov >= PointViewer.MinZoomLevel && cameraProperties.fov <= PointViewer.MaxZoomLevel) {
                cameraFOV = cameraProperties.fov;
            }

            this._viewer.setCameraProperties(cameraProperties.alpha, cameraProperties.beta);

            
        }

        this.camera.fov = cameraFOV;

        // Show the initial annotations
        this._selectedView = selectedView;
        this._initializeTripods();
        this._initializeAnnotations(initialAnnotations);

        this._switchingViews = false;

        // Fire the view changed event

        onViewChanged?.();
    }

    // ??? TODO ???

    //public updateOccludedAnnotations(annotations: IAnnotationDisplayItem[]) {
    //    if (!this._depthMap) {
    //        return annotations;
    //    }
    //    const cameraPosition = this._getVectorFromViewData(this._selectedView.Position);
    //    const occludedAnnotations = annotations.filter(ann => this._depthMap.isPositionOccluded(this._getVectorFromViewData(ann.Target3D), cameraPosition));

    //    this._occludedAnnotations = occludedAnnotations.map(ann => ann.ID);

    //    return occludedAnnotations;
    //}

    public getOccludedAnnotations(annotations: IAnnotationDisplayItem[]) {
        // returns empty for now. If desired, we could do some point cloud occlusion calculations.
        return [] as any;
    }

    public getScanSphereColor() {
        return this._options.scanSphereColor;
    }
    public getImageSphereColor() {
        return this._options.imageSphereColor;
    }

    public changeScanSphereColor(newColor: string) {
        if (this._options.scanSphereColor != newColor) {
            this._options.scanSphereColor = newColor ?? this.DefaultScanSphereColor;

            if (!this.isBusy) {
                this._initializeTripods(true);
            }
        }
    }

    public changeImageSphereColor(newColor: string) {
        if (this._options.imageSphereColor != newColor) {
            this._options.imageSphereColor = newColor ?? this.DefaultImageSphereColor;

            if (!this.isBusy) {
                this._initializeTripods(true);
            }
        }
    }

    public changeTagVolumeColor(newColor: string) {
        if (this._options.tagVolumeColor != newColor) {
            this._options.tagVolumeColor = newColor;
            this.boxManager?.setBoxesColor(newColor);
        }
    }

    public getTripodsDisplay() {
        return this._options.showTripods;
    }

    public setTripodsDisplay(display: boolean) {
        if (this._options.showTripods != display) {
            this._options.showTripods = display;

            if (!this.isBusy) {
                this._initializeTripods(true);
            }
        }
    }

    public getAnnotationsCount() {
        return this._labels.length;
    }

    public setAnnotations(annotations: IAnnotationDisplayItem[], selectedAnnotation: IAnnotationDisplayItem, readOnly:boolean, displayObstructed:boolean) {

        this._annotationsReadOnly = readOnly;
        {
            this._initializeAnnotations(annotations, selectedAnnotation, displayObstructed);
        }
    }

    public highlightView(viewID: string) {
        const sphere = this._tripodsByViewId.get(viewID);
        if (sphere) {
            this._highlightTripod(sphere);
        }
    }

    public clearHighlightedView() {
        if (this._highlightedTripod) {
            this._unhighlightTripod(this._highlightedTripod);
        }
    }


    public alignToTarget(target: IVector3D) {
        const targetVector = this._getVectorFromViewData(target);
        const curViewPosition = this._getVectorFromViewData(this._selectedView.Position);
        const directionToTarget = targetVector.subtract(curViewPosition)
        directionToTarget.normalize();

        this.viewer.alignCamera(directionToTarget);
    }

    public getViewerDirection() {

        return this._viewer?.cameraDirection;
    }

    private _getHorizontalNavigationDirection(from: BABYLON.Vector3, to: BABYLON.Vector3) {
        const direction = to.subtract(from);
        direction.y = 0;

        if (direction.lengthSquared() > 0.000001) {
            return direction.normalize();
        }

        const currentDirection = this._viewer?.cameraDirection?.clone();
        if (currentDirection) {
            currentDirection.y = 0;

            if (currentDirection.lengthSquared() > 0.000001) {
                return currentDirection.normalize();
            }
        }

        return new BABYLON.Vector3(0, 0, 1);
    }

    public setMeasurements(measurements: Measurement[]) {
        this._viewer?.setMeasurements(measurements);
    }
    public async setGraphicSources(graphicSources: IGraphicSource[]) {
        // Define the view bounds
        var viewBounds = new BABYLON.BoundingBox(
            new BABYLON.Vector3(
                this._selectedView.Position.X - (this._selectedView.Radius / this._selectedView.ScaleFactor),
                this._selectedView.Position.Y - (this._selectedView.Radius / this._selectedView.ScaleFactor),
                this._selectedView.Position.Z - (this._selectedView.Radius / this._selectedView.ScaleFactor)),
            new BABYLON.Vector3(
                this._selectedView.Position.X + (this._selectedView.Radius / this._selectedView.ScaleFactor),
                this._selectedView.Position.Y + (this._selectedView.Radius / this._selectedView.ScaleFactor),
                this._selectedView.Position.Z + (this._selectedView.Radius / this._selectedView.ScaleFactor))
        );

        // Separate intersecting and non-intersecting graphic sources
        const intersectingGraphicSources: IGraphicSource[] = [];
        const nonIntersectingGraphicSources: IGraphicSource[] = [];

        graphicSources.forEach((graphicSource) => {
            const min = new BABYLON.Vector3(
                graphicSource.ExtentsMin.X,
                graphicSource.ExtentsMin.Y,
                graphicSource.ExtentsMin.Z
            );

            const max = new BABYLON.Vector3(
                graphicSource.ExtentsMax.X,
                graphicSource.ExtentsMax.Y,
                graphicSource.ExtentsMax.Z
            );

            const boundingBox = new BABYLON.BoundingBox(min, max);

            if (boundingBox.intersectsMinMax(viewBounds.minimum, viewBounds.maximum)) {
                intersectingGraphicSources.push(graphicSource);
            } else {
                nonIntersectingGraphicSources.push(graphicSource);
            }
        });

         ;

        // Load intersecting sources
        await this._viewer.assemblyManager.loadGraphicsources(intersectingGraphicSources);
        await this._viewer.assemblyManager.unloadGraphicSources(nonIntersectingGraphicSources);
    }
    
    public async setAssemblies(assemblies: IAssembly[]) {

        await this._viewer.assemblyManager.updateAllAssemblies(assemblies);
    }

    public selectAssembly(assemblyID: string) {
        this._viewer.assemblyManager.selectAssembly(assemblyID);
    }

    private sleep(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private calculateAngle(x:number, y:number) {
        return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
    }

    private createPoints(pointsArray: any, colorsArray: any, material: any, pointSize: any, sliceIndex:number, isNearSlice:boolean) {

        let points = pointsArray;
        let colors = colorsArray;

        var pcs = new BABYLON.PointsCloudSystem("pcs", pointSize, this.scene) as any;

        var vertexData = new BABYLON.VertexData();
        vertexData.set(points, BABYLON.VertexBuffer.PositionKind);

        var ec = 1; //emissive color value 0 for UVs, 1 for color
        if (colors.length > 0) {
            ec = 1;
            vertexData.set(colors, BABYLON.VertexBuffer.ColorKind);
        }

        var mesh = new BABYLON.Mesh(pcs.name, pcs._scene);
        vertexData.applyToMesh(mesh, false);

        pcs.mesh = mesh;
        // free memory
        pcs._positions = null;
        pcs._uvs = null;
        pcs._colors = null;
        if (!pcs._updatable) {
            pcs.particles.length = 0;
        }

        mesh.material = material;
        mesh.isPickable = true;

        this._pointCloudManager.addPointCloud(sliceIndex + (isNearSlice ? "near" : "far") , pcs)
    }

    public async loadGLB(url: string) {

        
        try {
            var that = this;

            var wasmPath = Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + '/lod/draco_decoder.wasm';
            this.loadGLBWorker(url, wasmPath)
                .then((geometry) => {

                    var geo = geometry as any;
                })
                .catch((error) => {
                    console.error('Error:', error);
                });

        } finally {

        }
    }

    private loadGLBWorker(url: string, wasmPath: string) {
        return new Promise((resolve, reject) => {

            var worker = new Worker(new URL('../workers/GLBLoadWorker.ts', import.meta.url), { type: 'module' });

            worker.onmessage = function (e) {
                if (handleWorkerAuthRefreshMessage(worker, e)) {
                    return;
                }

                if (e.data.error) {
                    reject(new Error(e.data.error));
                } else {
                    resolve(e.data);
                }
            };

            worker.onerror = function (error) {
                reject(error);
            };

            worker.postMessage({ url, wasmPath, authContext: getCurrentWorkerAuthContext() });
        });
    }

    public async loadPointCloud(url: string, pointSize: number, sliceIndex:number, isNearSlice:boolean) {

        var pointCloudMaterial = new BABYLON.StandardMaterial("point cloud material", this.scene);
        pointCloudMaterial.emissiveColor = new BABYLON.Color3(1, 1, 1);
        pointCloudMaterial.disableLighting = true;
        pointCloudMaterial.pointsCloud = true;
        pointCloudMaterial.pointSize = pointSize;

        try {
            var that = this;

            var wasmPath = Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + '/lod/draco_decoder.wasm';
            this.loadPointCloudWorker(url, wasmPath)
                .then((geometry) => {
                    
                    var geo = geometry as any;
                    that.createPoints(geo[0], geo[1], pointCloudMaterial, 1, sliceIndex, isNearSlice);
                })
                .catch((error) => {
                    console.error('Error:', error);
                });

            //this._depthMap = await this.downloadAndDecompressDepthMap(this._depthMapUrl, this._abortController.signal);
        } finally {
            
        }
    }

    private loadPointCloudWorker(url : string, wasmPath:string) {
        return new Promise((resolve, reject) => {

            var worker = new Worker(new URL('../workers/PointLoadWorker.ts', import.meta.url), { type: 'module' });

            worker.onmessage = function (e) {
                if (handleWorkerAuthRefreshMessage(worker, e)) {
                    return;
                }

                if (e.data.error) {
                    reject(new Error(e.data.error));
                } else {
                    resolve(e.data);
                }
            };

            worker.onerror = function (error) {
                reject(error);
            };

            worker.postMessage({ url, wasmPath, authContext: getCurrentWorkerAuthContext() });
        });
    }
   
}

export default PointSceneManager;
